import differenceInYears from 'date-fns/differenceInYears';
import format from 'date-fns/format';
import {
    ICrmBookingDetails,
    ICrmBookingPassenger,
} from '../../../interfaces/booking/IBookingCrmPayload';
import { IBookingAgesPax, IBookingCruise } from '../../../interfaces/booking/IBookingCruise';
import {
    DirectBookingError,
    IDirectBookingConfirmPassenger,
    IDirectBookingConfirmPayload,
    IDirectBookingFormattedFlight,
    IDirectBookingRate,
} from '../../../interfaces/booking/IDirectBooking';
import {
    INSURANCE_COUNTRIES,
    NOT_AVAILABLE,
    CREATE_BOOKING_FEATURE,
    GET_RATES_FEATURE,
    GET_CABINS_FEATURE,
    GENDER_MALE,
    EMPTY_RATES,
} from './constants';
import { IBookingState, ISupportedFeaturesMap, InquiryQueryParams } from './reducer';
import { bookingDateFormat } from '../../components/sections/booking/dateFormat';
import { ActiveTest, IabTests } from '../abTests/activeTests';
import { DirectBookingErrorCodes } from '../../api/directBookingErrors';
import { ApiError, IApiValidationError } from '../../api';

type Passenger = {
    age: number;
    birthDate: string | null;
};

type PassengersAgeMap = {
    childs: Passenger[];
    adults: Passenger[];
};

export const isInitialPassengerDataEmpty = (passengers: ICrmBookingPassenger[]) => {
    return (
        !passengers?.length ||
        passengers.some(
            (passenger) => !passenger.firstName || passenger.firstName === NOT_AVAILABLE,
        )
    );
};

export const getPassengersByAgeMap = (passengers: Passenger[], ageConfig: IBookingAgesPax) => {
    return passengers.reduce(
        (acc, passenger) => {
            if (passenger.age > ageConfig.child.max) {
                acc.adults.push(passenger);
            } else {
                acc.childs.push(passenger);
            }
            return acc;
        },
        { adults: [], childs: [] } as PassengersAgeMap,
    );
};

export const getAgeByDateDifference = (birthDate: string, destinationDate?: string) => {
    const compareDate = destinationDate ? new Date(destinationDate) : new Date();
    return differenceInYears(compareDate, new Date(birthDate));
};

export const getNormalizedPassengersMap = (
    passengers: { birthDate?: string }[],
    ageConfig: IBookingAgesPax,
    departureDate?: string,
) => {
    const ages = passengers.map((p) => ({
        age: p.birthDate
            ? getAgeByDateDifference(p.birthDate, departureDate)
            : ageConfig.adult.min || 18,
        birthDate: p.birthDate || null,
    }));

    return getPassengersByAgeMap(ages, ageConfig);
};

export const enableInsurance = (passengers: { nationality?: string | null }[]) => {
    return passengers.every((passenger) =>
        INSURANCE_COUNTRIES.includes(passenger.nationality?.toUpperCase() || ''),
    );
};

export const enableFlights = (flightInfo: ICrmBookingDetails['flightInfo']) => {
    if (!flightInfo) {
        return false;
    }
    return flightInfo.flights.some(
        (flight) =>
            flight.departureAirport &&
            flight.departureAirport.code &&
            flight.departureAirport.code !== NOT_AVAILABLE,
    );
};

export const getTrackingDataToPush = (): Record<string, unknown> | null => {
    const data = window.sessionStorage.getItem('__dataLayer');

    if (!data) {
        return null;
    }

    try {
        const parsedData = JSON.parse(data);
        const originalLocation = window.location.href.split('?')[0];

        return {
            ...parsedData,
            originalLocation,
            pageType: 'booking funnel',
        };
    } catch (error) {
        return null;
    }
};

export const removeTrackingDataFromStorage = () => {
    window.sessionStorage.removeItem('__dataLayer');
};

export const getSupportedFeaturesMap = (features: string[] = []): ISupportedFeaturesMap => {
    return {
        isBookingAvailable: features.includes(CREATE_BOOKING_FEATURE),
        isRatesAvailable: features.includes(GET_RATES_FEATURE),
        isCabinAvailable: features.includes(GET_CABINS_FEATURE),
    };
};

export const getRatesWithTwoWayFlights = (
    rates: IDirectBookingRate[],
): IDirectBookingFormattedFlight[] => {
    return rates.reduce((acc, rate) => {
        const flight = rate.includedComponents.find((e) => e.type === 'AIR');

        if (
            flight &&
            flight.details.inboundFlights.length &&
            flight.details.outboundFlights.length
        ) {
            const { details, ...rest } = flight;

            acc.push({
                rateTotalPrice: rate.price,
                flightPrice: rate.priceBreakdown.transportation,
                inboundFlights: details.inboundFlights,
                outboundFlights: details.outboundFlights,
                ...rest,
            });
            return acc;
        }

        return acc;
    }, [] as IDirectBookingFormattedFlight[]);
};

export const mapStateToDirectBookingPayload = (
    bookingState: IBookingState,
): IDirectBookingConfirmPayload => {
    const { crmData, pricesData, passengerStepData, flightStepData, paymentStepData } =
        bookingState;

    if (!crmData || !pricesData || !passengerStepData) {
        throw new Error('Cannot create booking confirm data.');
    }

    let components: IDirectBookingConfirmPayload['components'] = pricesData.includedComponents;

    if (flightStepData.flightInfo) {
        components = components.filter((i) => i.type !== 'AIR');
        components.push({
            code: flightStepData.flightInfo.code,
            type: flightStepData.flightInfo.type,
        });
    }

    return {
        company: crmData.cruiselineCode,
        cruiseId: crmData.cruiseId,
        sailId: crmData.sailId,
        cabinCategoryId: crmData.cabinTypeId,
        cabinNumber: crmData.cabinNumber || '',
        rateCode: pricesData.rateCode,
        dlBookingId: crmData.bookingId,
        components,

        serviceCharges: pricesData.priceBreakdown.serviceCharges,

        passengers: passengerStepData.map((passenger) => {
            const passengerPayload: IDirectBookingConfirmPassenger = {
                gender: passenger.gender.toUpperCase(),
                firstName: passenger.firstName,
                lastName: passenger.lastName,
                birthday: passenger.birthDate,
                countryOfBirthCode: passenger.nationality,
                nationalityCode: passenger.nationality,
                identification: [],
            };

            if (passenger.documentType && passenger.documentNumber) {
                passengerPayload.identification = [
                    {
                        documentType: passenger.documentType,
                        documentNumber: passenger.documentNumber,
                        issuedAt: passenger.issuedAt.toUpperCase(),
                        issuedDate: passenger.issuedDate,
                        expireDate: passenger.expireDate,
                    },
                ];
            }

            return passengerPayload;
        }),

        billingDetails: {
            firstName: paymentStepData.recipientInfo?.firstName || '',
            lastName: paymentStepData.recipientInfo?.lastName || '',
            address: {
                street: paymentStepData.recipientInfo?.address || '',
                email: paymentStepData.recipientInfo?.email || '',
                city: paymentStepData.recipientInfo?.city || '',
                countryCode: paymentStepData.recipientInfo?.country || '',
                postalCode: paymentStepData.recipientInfo?.postal || '',
                telephone: `${paymentStepData.recipientInfo?.countryCodeNum}${paymentStepData.recipientInfo?.phone}`,
            },
        },
    };
};

export const normalizeInitialBookingData = (
    data: ICrmBookingDetails,
    fallbackData: { code: string },
) => {
    return {
        ...data,
        passengers: data.passengers.map((passenger) => ({
            ...passenger,
            birthDate: passenger.birthDate ? bookingDateFormat(passenger.birthDate) : '',
            nationality: passenger.nationality?.toUpperCase() || fallbackData.code.toUpperCase(),
        })),
        booker: {
            ...data.booker,
            nationality: data.booker.nationality?.toUpperCase() || fallbackData.code.toUpperCase(),
        },
    };
};

export const getInitialDataFromQueryParams = (
    query: Record<string, unknown>,
): null | ICrmBookingDetails => {
    const numChild = Number(query['numChild']) || 0;
    const numAdult = Number(query['numAdult']) || 0;
    const includesFlight = query['includesFlight'] === 'true';
    const departureAirport = query['selectedAirportCode'] as string | undefined;
    const rateCode = query['rateCode'] as string | undefined;

    if ((!numChild && !numAdult) || !rateCode) {
        return null;
    }

    const initialData = {
        bookingType: 'DIRECT_BOOKING',
        publicToken: '',
        isAllInclusive: query['isAllIn'] === 'true',
        cruiselineCode: query['cruiseCode'],
        cruiseId: Number(query['cruiseNid']),
        sailId: Number(query['sailNid']),
        cabinTypeId: Number(query['cabinNid']),
        rateCode,
        rateDesc: '',
        cruiseUrl: '',
        bookingId: '',
        cruisePrice: 0,
        totalPrice: 0,
        booker: {} as ICrmBookingDetails['booker'],
        message: '',
    } as ICrmBookingDetails;

    const passengers = Array.from({ length: numChild + numAdult }, (_, i) => {
        const passengerNumber = i + 1;
        const dob = query[`p${passengerNumber}Birth`] as string | undefined;

        return {
            firstName: '',
            lastName: '',
            birthDate: dob || '',
            gender: GENDER_MALE,
            nationality: null,
        };
    });

    initialData.passengers = passengers;

    if (includesFlight && departureAirport) {
        initialData.flightInfo = {
            flights: [
                {
                    departureAirport: {
                        code: departureAirport,
                    },
                    arrivalAirport: {
                        code: departureAirport,
                    },
                },
            ],
        };
    }

    return initialData;
};

export const getTrackingAbTestsValues = (testData: ActiveTest | undefined) => {
    if (!testData) {
        return 'not_set';
    }
    if (!testData.variationGroup) {
        return 'not_set';
    }
    return testData.variationGroup === testData.newVariationId
        ? `${testData.name}_on`
        : `${testData.name}_off`;
};

export const getGeneralTrackingData = ({
    cruiseData,
    pricesData,
    inquiry,
    passengerCount,
    abTests,
    step,
}: {
    step: string;
    inquiry: null | InquiryQueryParams;
    cruiseData: IBookingCruise;
    pricesData?: null | IDirectBookingRate;
    passengerCount: { adults: number; childs: number };
    abTests?: IabTests;
}) => {
    let totalPrice = 0;
    let currency = '';
    let includesFlight = false;

    if (pricesData) {
        includesFlight = pricesData.includedComponents.some((i) => i.type === 'AIR');
        totalPrice = pricesData.price.amount;
        currency = pricesData.price.currency;
    } else if (inquiry) {
        includesFlight = !!inquiry.includesFlight;
        totalPrice = inquiry.totalPrice;
        currency = inquiry.currency;
    }

    return {
        step,
        cabin_type: cruiseData?.sail.cabin.title,
        company_name: cruiseData?.operator.title,
        cruise_name: cruiseData?.title,
        currency,
        date_start: format(new Date(cruiseData?.sail.departureDate), 'dd.M.yyyy'),
        date_end: format(new Date(cruiseData?.sail.arrivalDate), 'dd.M.yyyy'),
        destination_name: cruiseData?.zones[0]?.title,
        flight_option: includesFlight ? 'incl' : '',
        harbor_start: cruiseData.harbours[0]?.title,
        harbor_end: cruiseData.harbours[cruiseData.harbours.length - 1]?.title,
        sail_id: cruiseData.sail.nid,
        nights_number: cruiseData?.nights.toString(),
        product_id_1: cruiseData?.nid.toString(),
        quantity: passengerCount.adults,
        ship_name: cruiseData?.ship.title,
        transactionTotal: totalPrice,
        abtest: Object.values(abTests || [])
            .map(getTrackingAbTestsValues)
            .join(','),
        isEstimatedPrice: !!inquiry?.isEstimatedPrice,
    };
};

export const getThankYouPageDataLayer = ({
    cruiseData,
    pricesData,
    bookingId,
    inquiry,
    isInquiryFlow,
    passengerCount,
    event,
    abTests,
    step,
}: {
    event?: string;
    step: string;
    bookingId: string;
    inquiry: null | InquiryQueryParams;
    isInquiryFlow?: boolean;
    cruiseData: IBookingCruise;
    pricesData?: null | IDirectBookingRate;
    passengerCount: { adults: number; childs: number };
    abTests?: IabTests;
}) => {
    const eventName = event ?? 'BookOnline-Start';

    const isInquiryBooking = isInquiryFlow || eventName === 'VPV-AdWordsConversion';

    return {
        site_variant: 'mobile-vpv',
        event: isInquiryBooking ? 'VPV-AdWordsConversion' : eventName,
        type: isInquiryBooking ? 'inquiry' : 'booking',
        page_type: 'booking-thank-you',
        transactionAffiliation: 'Mobile-Website-Request',
        transactionId: bookingId,

        ...getGeneralTrackingData({
            cruiseData,
            pricesData,
            inquiry,
            passengerCount,
            abTests,
            step,
        }),
    };
};

export const getDirectBookingFetchError = (errorPayload: unknown) => {
    if (errorPayload instanceof Error && errorPayload.message === EMPTY_RATES) {
        return {
            name: DirectBookingErrorCodes[DirectBookingErrorCodes['RATE_CODE_NOT_AVAILABLE']],
            code: DirectBookingErrorCodes['RATE_CODE_NOT_AVAILABLE'],
        };
    }

    if (errorPayload && typeof errorPayload === 'object' && 'code' in errorPayload) {
        const directBookingError = errorPayload as DirectBookingError['error'];
        return {
            name: directBookingError.name,
            code: DirectBookingErrorCodes[directBookingError.name] || 0,
        };
    }

    if (errorPayload instanceof ApiError && errorPayload.details.payload) {
        const payload = errorPayload.details.payload;

        if (typeof payload === 'object' && 'bookingFlow' in payload) {
            const directBookingError = payload as DirectBookingError;
            return {
                name: directBookingError.error.name,
                code: DirectBookingErrorCodes[directBookingError.error.name] || 0,
            };
        }
    }

    return;
};

export const getApiValidationErrors = (errorPayload: unknown) => {
    const isApiError = errorPayload instanceof ApiError && errorPayload.details.payload;

    if (!isApiError) {
        return [];
    }

    const payload = errorPayload.details.payload as IApiValidationError | string;

    const isPayloadValid = typeof payload === 'object';
    const hasErrorsData = isPayloadValid ? 'errors' in payload : false;
    const isErrorsArray = isPayloadValid && hasErrorsData ? Array.isArray(payload.errors) : false;

    if (isPayloadValid && hasErrorsData && isErrorsArray) {
        const flatErrorsList = payload.errors.reduce(
            (acc, val) => {
                const { children, ...rest } = val;

                if (children && children.length) {
                    acc = [...acc, ...children];
                }

                if (val?.constraints) {
                    acc.push(rest);
                }

                return acc;
            },
            [] as IApiValidationError['errors'],
        );

        return flatErrorsList.map((e) => {
            const constraintsList = e.constraints ? Object.keys(e.constraints) : [];
            const firstErrorKey = constraintsList[0];
            const context = e.contexts ? e.contexts[firstErrorKey] : null;

            return {
                field: e.property,
                errorKey: context ? context.translationKeyV2 : e.constraints?.[firstErrorKey] || '',
            };
        });
    }

    return [];
};
